import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import RestockReconcile from './RestockReconcile';
import './restock-admin.css';

function titleCase(value) {
  return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function incidentLabel(request) {
  return request?.incident_order_number ? `${request.incident} — ${request.incident_order_number}` : request?.incident || '';
}

const OPEN_STATUSES = new Set(['pending_approval','needs_changes','approved','sent_to_logistics','ordered','received','restocked']);

function ReconciliationAudit({ request }) {
  const audit = Array.isArray(request?.reconciliation) ? request.reconciliation : [];
  if (!audit.length) return null;
  return (
    <details className="restock-admin-reconciliation">
      <summary>Inventory reconciliation · {request.reconciled_by_name || 'Admin'} · {formatWhen(request.reconciled_at)}</summary>
      <div>
        {audit.map((item, index) => (
          <div className="restock-admin-reconcile-row" key={`${item.assetId}-${item.catalogId}-${index}`}>
            <span>
              <strong>{item.item}</strong>
              <small>{item.assetName || item.assetId} · used {item.reportedUsed} · final {item.finalQty} / standard {item.standardQty || '—'}</small>
            </span>
            <span>
              {(Array.isArray(item.oldLots) ? item.oldLots : []).filter(lot => Number(lot.consumedQty) > 0).map(lot => (
                <small key={lot.recordId}>Consumed {lot.consumedQty}{lot.lotSerial ? ` · lot ${lot.lotSerial}` : ''}{lot.expirationDate ? ` · exp ${lot.expirationDate}` : ''}</small>
              ))}
              {(Array.isArray(item.replacements) ? item.replacements : []).map(line => (
                <small key={line.recordId}>Added {line.quantity}{line.lotSerial ? ` · lot ${line.lotSerial}` : ''}{line.expirationDate ? ` · exp ${line.expirationDate}` : ' · N/A exp'}</small>
              ))}
              {Number(item.untrackedConsumed) > 0 && <small>Untracked prior lot consumed: {item.untrackedConsumed}</small>}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}

export default function RestockAdmin() {
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [workingId, setWorkingId] = useState('');
  const [reconciling, setReconciling] = useState(null);
  const [completion, setCompletion] = useState(null);

  const load = async () => {
    setError('');
    setStatus('loading');
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      setStatus('signed-out');
      return;
    }
    const { data, error: rpcError } = await supabase.rpc('admin_list_restock_requests');
    if (rpcError) {
      setError(rpcError.message || 'Unable to load restock requests.');
      setStatus('error');
      return;
    }
    setRequests(Array.isArray(data) ? data : []);
    setStatus('ready');
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(
    () => showClosed ? requests : requests.filter(item => OPEN_STATUSES.has(item.status)),
    [requests, showClosed],
  );

  const counts = useMemo(() => ({
    pending: requests.filter(item => item.status === 'pending_approval').length,
    logistics: requests.filter(item => ['approved','sent_to_logistics','ordered'].includes(item.status)).length,
    restock: requests.filter(item => ['received','restocked'].includes(item.status)).length,
  }), [requests]);

  const updateStatus = async (request, nextStatus) => {
    setWorkingId(request.id);
    setError('');
    const { error: rpcError } = await supabase.rpc('admin_update_restock_status', {
      p_request_id: request.id,
      p_status: nextStatus,
    });
    setWorkingId('');
    if (rpcError) {
      setError(rpcError.message || 'Unable to update restock request.');
      return;
    }
    await load();
  };

  const finishReconciliation = async result => {
    setReconciling(null);
    setCompletion(result || {});
    await load();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (status === 'signed-out') {
    return (
      <section className="inventory-card">
        <h2>Restock Requests</h2>
        <p className="inventory-muted">Member sign-in is also required to review or update restock requests.</p>
        <button type="button" className="inventory-primary-action" onClick={() => window.location.assign('/')}>Open member sign in</button>
      </section>
    );
  }

  if (reconciling) {
    return <RestockReconcile request={reconciling} onCancel={() => setReconciling(null)} onComplete={finishReconciliation} />;
  }

  return (
    <section className="inventory-card restock-admin-root">
      <div className="inventory-section-head">
        <div>
          <h2>Restock Requests</h2>
          <p>Used-supply requests stay here even if an email is missed. Once replacement supplies arrive, reconcile the actual bag inventory before returning it to Ready.</p>
        </div>
        <div className="restock-admin-actions">
          <button type="button" onClick={() => setShowClosed(current => !current)}>{showClosed ? 'Hide closed' : 'Show closed'}</button>
          <button type="button" onClick={load} disabled={status === 'loading'}>Refresh</button>
        </div>
      </div>

      {error && <div className="inventory-error">{error}</div>}
      {completion && (
        <div className={completion.warning ? 'restock-admin-note warning' : 'inventory-scan-success'}>
          <strong>Inventory reconciliation saved.</strong>{' '}
          {completion.readiness?.ready?.length ? `${completion.readiness.ready.length} affected bag${completion.readiness.ready.length === 1 ? '' : 's'} returned to Ready.` : 'The restock request is now reconciled.'}
          {completion.readiness?.skipped?.length ? ` ${completion.readiness.skipped.length} bag${completion.readiness.skipped.length === 1 ? '' : 's'} stayed out of Ready because another condition still needs attention.` : ''}
          {completion.warning ? ` ${completion.warning}` : ''}
          <button type="button" className="restock-admin-inline-dismiss" onClick={() => setCompletion(null)}>Dismiss</button>
        </div>
      )}

      <div className="restock-admin-summary">
        <div><small>Awaiting approval</small><strong>{counts.pending}</strong></div>
        <div><small>Order / Logistics</small><strong>{counts.logistics}</strong></div>
        <div><small>Received / Restock</small><strong>{counts.restock}</strong></div>
      </div>

      {status === 'loading' && <div className="inventory-empty">Loading restock requests…</div>}
      {status === 'ready' && visible.length === 0 && <div className="inventory-empty">No restock requests in this view.</div>}

      {status === 'ready' && visible.map(request => (
        <article className="restock-admin-card" key={request.id}>
          <div className="restock-admin-head">
            <div>
              <span>{request.request_code || request.id}</span>
              <h3>{incidentLabel(request)}</h3>
              <p>{request.person} · {request.deployment_type || 'Deployment'} · submitted {formatWhen(request.submitted_at)}</p>
            </div>
            <span className={`restock-admin-pill status-${request.status}`}>{titleCase(request.status)}</span>
          </div>

          <div className="restock-admin-items">
            {(Array.isArray(request.items) ? request.items : []).map((item, index) => (
              <div key={`${item.assetId}-${item.catalogId}-${index}`}>
                <span><strong>{item.item}</strong><small>{item.assetName || item.assetId}</small></span>
                <strong>Qty {item.quantity}</strong>
              </div>
            ))}
          </div>

          <div className="restock-admin-meta">
            <span><small>Submitted by</small><strong>{request.submitted_by_name}</strong></span>
            <span><small>Approval</small><strong>{request.approved_by_name ? `${request.approved_by_name} · ${formatWhen(request.approved_at)}` : titleCase(request.approval_email_status)}</strong></span>
            <span><small>Logistics email</small><strong>{titleCase(request.logistics_send_status)}</strong></span>
          </div>

          {request.decision_note && <div className="restock-admin-note"><strong>Decision note:</strong> {request.decision_note}</div>}
          {request.approval_email_error && <div className="restock-admin-note warning"><strong>Approval email:</strong> {request.approval_email_error}</div>}
          {request.logistics_email_error && <div className="restock-admin-note warning"><strong>Logistics email:</strong> {request.logistics_email_error}</div>}
          <ReconciliationAudit request={request} />

          <div className="restock-admin-buttons">
            {request.status === 'approved' && <button type="button" disabled={workingId === request.id} onClick={() => updateStatus(request, 'ordered')}>Mark Ordered Manually</button>}
            {request.status === 'sent_to_logistics' && <button type="button" disabled={workingId === request.id} onClick={() => updateStatus(request, 'ordered')}>Mark Ordered</button>}
            {request.status === 'ordered' && <button type="button" disabled={workingId === request.id} onClick={() => updateStatus(request, 'received')}>Mark Received</button>}
            {request.status === 'received' && <button type="button" className="restock-admin-reconcile-button" onClick={() => { setCompletion(null); setReconciling(request); }}>Reconcile Inventory & Restock</button>}
            {request.status === 'restocked' && <button type="button" disabled={workingId === request.id} onClick={() => updateStatus(request, 'closed')}>Close Request</button>}
            {['pending_approval','needs_changes'].includes(request.status) && <span>Use the authenticated approval page from the team email to approve or request changes.</span>}
            {request.status === 'approved' && request.logistics_send_status !== 'sent' && <span>Use this after placing the order manually while direct Logistics email is not configured.</span>}
            {request.status === 'received' && <span>Do not mark this Restocked manually. The reconciliation flow confirms consumed lots and replacement inventory first.</span>}
          </div>
        </article>
      ))}
    </section>
  );
}
